/**
 * eStream Wire Protocol - Browser Implementation
 *
 * Same binary format as native UDP clients.
 * See: crates/estream-wire/src/header.rs
 */
// Wire protocol constants
export const MAGIC = 0x45535452; // "ESTR" in little-endian
export const VERSION = 0x01;
export const HEADER_SIZE = 16;
// Packet types (matches Rust PacketType enum)
export const PacketType = {
    Emit: 0x01,
    Query: 0x02,
    Response: 0x03,
    Batch: 0x04,
    Heartbeat: 0x10,
    Hello: 0x20,
    Goodbye: 0x21,
    SmartCircuit: 0x30,
    SmartCircuitResult: 0x31,
    KeyRegistration: 0x40,
    NodeAnnouncement: 0x41,
    // Spark authentication
    SparkChallengeRequest: 0x50,
    SparkChallenge: 0x51,
    SparkAuthRequest: 0x52,
    SparkSessionGrant: 0x53,
    SparkSessionRevoke: 0x54,
    // Subscriptions
    Subscribe: 0x60,
    Unsubscribe: 0x61,
    Subscribed: 0x62,
    Unsubscribed: 0x63,
    Event: 0x64,
    // Session management
    SessionPing: 0x70,
    SessionPong: 0x71,
    SessionExpired: 0x72,
};
// Header flags
export const Flags = {
    SIGNED: 1 << 0,
    ENCRYPTED: 1 << 1,
    COMPRESSED: 1 << 2,
    PRIORITY: 1 << 3,
    BATCH: 1 << 4,
};
/**
 * Encode a wire header to bytes
 */
export function encodeHeader(packetType, payloadLen, flags = 0) {
    const buffer = new ArrayBuffer(HEADER_SIZE);
    const view = new DataView(buffer);
    const nonce = new Uint8Array(4);
    crypto.getRandomValues(nonce);
    view.setUint32(0, MAGIC, true); // magic (little-endian)
    view.setUint8(4, VERSION); // version
    view.setUint8(5, packetType); // packet_type
    view.setUint16(6, flags, true); // flags (little-endian)
    view.setUint32(8, payloadLen, true); // payload_len (little-endian)
    const result = new Uint8Array(buffer);
    result.set(nonce, 12); // nonce
    return result;
}
/**
 * Decode a wire header from bytes
 */
export function decodeHeader(data) {
    if (data.length < HEADER_SIZE) {
        return null;
    }
    const view = new DataView(data.buffer, data.byteOffset);
    const magic = view.getUint32(0, true);
    if (magic !== MAGIC) {
        console.warn('Invalid magic:', magic.toString(16));
        return null;
    }
    const version = view.getUint8(4);
    if (version !== VERSION) {
        console.warn('Unsupported version:', version);
        return null;
    }
    return {
        magic,
        version,
        packetType: view.getUint8(5),
        flags: view.getUint16(6, true),
        payloadLen: view.getUint32(8, true),
        nonce: data.slice(12, 16),
    };
}
/**
 * Encode a Subscribe payload
 */
export function encodeSubscribe(sessionToken, topic) {
    const topicBytes = new TextEncoder().encode(topic + '\0');
    const payload = new Uint8Array(32 + topicBytes.length);
    payload.set(sessionToken.slice(0, 32), 0);
    payload.set(topicBytes, 32);
    return payload;
}
/**
 * Encode an Emit payload (simplified - topic + data)
 */
export function encodeEmit(topic, data) {
    const topicBytes = new TextEncoder().encode(topic);
    const topicLen = topicBytes.length;
    // Format: topic_len (u16) | topic | data
    const payload = new Uint8Array(2 + topicLen + data.length);
    const view = new DataView(payload.buffer);
    view.setUint16(0, topicLen, true);
    payload.set(topicBytes, 2);
    payload.set(data, 2 + topicLen);
    return payload;
}
export function decodeEvent(data) {
    if (data.length < 16) {
        return null;
    }
    const view = new DataView(data.buffer, data.byteOffset);
    const subscriptionId = view.getUint32(0, true);
    const timestamp = Number(view.getBigUint64(4, true));
    const topicLen = view.getUint16(12, true);
    const payloadLen = view.getUint16(14, true);
    if (data.length < 16 + topicLen + payloadLen) {
        return null;
    }
    const topic = new TextDecoder().decode(data.slice(16, 16 + topicLen));
    const payload = data.slice(16 + topicLen, 16 + topicLen + payloadLen);
    return {
        subscriptionId,
        timestamp,
        topic,
        data: payload,
    };
}
//# sourceMappingURL=wire.js.map